"use client";

import { useState, useId } from "react";
import type { Product } from "@/lib/data";
import { ProductCard } from "./product-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/lib/i18n/language-context";

interface ProductGridProps {
  products: Product[];
  columns?: number;
}

export function ProductGrid({
  products,
  columns = 4
}: ProductGridProps) {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const skeletonId = useId(); // Use a stable ID instead of Date.now()

  const gridColsClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
  }[columns] || "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

  return (
    <div className={`grid ${gridColsClass} gap-4 md:gap-6`}>
      {isLoading ? (
        // Loading skeleton - using stable IDs to avoid hydration issues
        Array(8)
          .fill(null)
          .map((_, index) => (
            <div key={`skeleton-${index}-${skeletonId}`} className="space-y-2">
              <Skeleton className="aspect-square rounded-lg" />
              <Skeleton className="h-6 w-2/3 rounded-lg" />
              <Skeleton className="h-4 w-1/3 rounded-lg" />
              <div className="flex gap-2">
                <Skeleton className="h-9 w-1/2 rounded-lg" />
                <Skeleton className="h-9 w-1/2 rounded-lg" />
              </div>
            </div>
          ))
      ) : products.length > 0 ? (
        // Product cards
        products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))
      ) : (
        // No products found
        <div className="col-span-full text-center py-12">
          <h3 className="text-lg font-medium">{t("common.noProductsFound")}</h3>
          <p className="text-muted-foreground">
            {t("common.tryAdjustingFilters")}
          </p>
        </div>
      )}
    </div>
  );
}
